import type { ReadonlyDeep } from 'type-fest';
import type { DataItem as DispatchJobsTableDataItem } from '@/components/tables/DispatchJobsTable.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

// Each action keeps its own check function even when the current rule is
// identical to a sibling's — the per-action rules are expected to diverge
// (e.g. lock vs. cancel will grow distinct conditions), so deduplicating
// now would force a re-split later.
type DispatchJobPredicateData = ReadonlyDeep<
  Pick<DispatchJobsTableDataItem, 'isLocked'> & {
    originalJob: Pick<
      DispatchJobsTableDataItem['originalJob'],
      'allocation_state'
    >;
  }
>;

export function checkReadyToAllocate(
  dispatchJob: DispatchJobsTableDataItem,
): boolean {
  return dispatchJob.isReadyToAllocate;
}

export function checkReadyToPreAllocate(
  dispatchJob: DispatchJobPredicateData,
): boolean {
  return (
    !dispatchJob.isLocked &&
    dispatchJob.originalJob.allocation_state !== 'allocated'
  );
}

export function checkReadyToDeallocate(
  dispatchJob: DispatchJobPredicateData,
): boolean {
  return (
    !dispatchJob.isLocked &&
    dispatchJob.originalJob.allocation_state !== 'deallocated'
  );
}

export function checkReadyToAllocateAndSplit(
  dispatchJob: DispatchJobPredicateData,
): boolean {
  return (
    !dispatchJob.isLocked &&
    dispatchJob.originalJob.allocation_state !== 'allocated'
  );
}

export function checkReadyToLock(
  dispatchJob: DispatchJobPredicateData,
): boolean {
  return (
    !dispatchJob.isLocked &&
    dispatchJob.originalJob.allocation_state !== 'allocated'
  );
}

export function checkReadyToUnlock(
  dispatchJob: ReadonlyDeep<Pick<DispatchJobsTableDataItem, 'isLocked'>>,
): boolean {
  return dispatchJob.isLocked;
}

// The driver assigned to a despatch job, for the `deallocate_driv` action.
// A pre-allocated job carries its driver on stop 1 (stop 2 is empty), and an
// allocated job carries it on the served stop(s), so take the first stop that
// has one rather than a fixed stop. Returns undefined when none is assigned.
export function getDispatchJobDriver(
  job: ReadonlyDeep<{
    stops: Pick<
      DispatchJobsTableDataItem['originalJob']['stops'][number],
      'driver_is'
    >[];
  }>,
): number | undefined {
  for (const stop of job.stops) {
    if (stop.driver_is) return stop.driver_is;
  }
  return undefined;
}

// Build the bulk-allocation item for deallocating a job. A pre-allocated job
// deallocates without a driver (`deallocate`); an allocated job must deallocate
// its specific assigned driver (`deallocate_driv`).
export function getDeallocateActionItem(
  job: ReadonlyDeep<
    Pick<
      DispatchJobsTableDataItem['originalJob'],
      'rockhopper_id' | 'rockhopper_date' | 'allocation_state' | 'stops'
    >
  >,
): openapi.JobAllocationCreateBodyOpenapi {
  const base = {
    job_id: job.rockhopper_id,
    job_date: job.rockhopper_date,
  };
  if (job.allocation_state === 'preallocated') {
    return { ...base, action: 'deallocate' };
  }
  return {
    ...base,
    action: 'deallocate_driv',
    driver: getDispatchJobDriver(job),
  };
}

export function checkReadyToPickup(
  dispatchJob: DispatchJobPredicateData,
): boolean {
  return (
    !dispatchJob.isLocked &&
    dispatchJob.originalJob.allocation_state === 'allocated'
  );
}

export function checkReadyToSplitToBase(
  dispatchJob: DispatchJobPredicateData,
): boolean {
  return (
    !dispatchJob.isLocked &&
    dispatchJob.originalJob.allocation_state === 'allocated'
  );
}

export function checkReadyToPoD(
  dispatchJob: DispatchJobPredicateData,
): boolean {
  return (
    !dispatchJob.isLocked &&
    dispatchJob.originalJob.allocation_state === 'allocated'
  );
}

export function checkReadyToEditTimestamps(
  dispatchJob: ReadonlyDeep<Pick<DispatchJobsTableDataItem, 'isLocked'>>,
): boolean {
  return !dispatchJob.isLocked;
}

export function checkReadyToClone(
  dispatchJob: DispatchJobsTableDataItem,
): boolean {
  return true;
}

export function checkReadyToCancel(
  dispatchJob: DispatchJobPredicateData,
): boolean {
  return (
    !dispatchJob.isLocked &&
    dispatchJob.originalJob.allocation_state !== 'allocated'
  );
}

export function checkReadyToFutile(
  dispatchJob: DispatchJobPredicateData,
): boolean {
  return (
    !dispatchJob.isLocked &&
    dispatchJob.originalJob.allocation_state === 'allocated'
  );
}

export function checkReadyToQuery(
  dispatchJob: DispatchJobsTableDataItem,
): boolean {
  return true;
}
